<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Customer"
        :baseView="baseView"
        :primaryLabel="model.Name"
        :secondaryLabel="baseView.id"
        recordType="Customer"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-list" v-if="loaded">
      <div class="data-grid-details">
        <GcDataGrid
          :grouping="true"
          :export="true"
          :summary="true"
          :dataSourceConfig="
            screenJson.gridData
              ? screenJson.gridData.gridOptions.dataSource
              : undefined
          "
          :columns="getColumns"
          :valueChangeListners="getListners"
          :childGridColumns="getChildGridColumns"
          :gridToolbar="
            screenJson.gridData ? screenJson.gridData.toolbar : undefined
          "
          :padColumn="true"
          :id="'transaction-list-grid'"
          v-if="loaded && screenJson.gridData"
          ref="transactionListGrid"
          :isEditableGrid="true"
          :rowClick="rowClickCallBack"
          :showFooter="false"
          :gridOptions="
            screenJson.gridData ? screenJson.gridData.gridOptions : undefined
          "
          :elementAttrClass="'transaction-template'"
          :paging="true"
          :heightPadding="20"
        />
      </div>
    </div>
  </gc-main-layout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import CustomerTabsMixin from "@/_mixins/customer-tabs.mixin";
import DataGridMixin from "../../_mixins/data-grid.mixin";
import { CustomerService } from "../../_services/api/customers/customer.service";
import { CacheService } from "../../_services/cache/cache-service";
import { SalesOrderListService } from "../../_services/api/sales/sales-order-list.service";

import { CommonStorage } from "../../_services/storage";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

function logDev() {
  // eslint-disable-next-line no-console
  console.log("CX Log >>> ", arguments);
}

export default {
  props: {
    /**
     * contains report configurations
     */
    reportConfig: {
      type: Object,
      default: undefined,
    },
  },
  mixins: [RouteMixin, CustomerTabsMixin, DataGridMixin],
  components: {},

  data() {
    return {
      persistenceKey: "customer-surveys_list",
      backButtonRoute: `/customer`,
      CustomerSalesRepresentativeCode: undefined,
      model: {
        Code: "",
        Name: "",
      },

      gridOptions: {
        noDataMessage: {
          addNew: {
            class: "no-data",
            title: "No Transactions",
            message:
              "Currently there are no surveys for this customer. Give them a call and sell them something today!",
          },
        },
      },
      loaded: false,
    };
  },
  computed: {
    defaultDateFilterValue() {
      // let d = new Date();
      // d.setMonth(d.getMonth() - 3);
      // return d;

      return new Date();
    },
    getCustomerRepDefault() {
      if (this.CustomerSalesRepresentativeCode)
        return [this.CustomerSalesRepresentativeCode];
      else return false;
    },
    getChildGridColumns() {
      return this.screenJson.gridData.childColumns;
    },
    getListners() {
      return this.screenJson.gridData
        ? this.screenJson.gridData.valueChangedEventListners
        : undefined;
    },
    screenJson() {
      var config = {
        title: "Customers",

        exportFileName: "Customer Surveys",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "SurveyResponseList",
              filter: [
                ["CustomerCode", "=", this.baseView.id],
                "and",
                ["Status", "<>", "X"],
              ],

              serverPaging: true,
              allowGrouping: false,
              select: ["RecordId"].concat(
                this.getSelectFields(this.getColumns)
              ),
              postProcessCallback: this.surveyResponseListPostProcessCallback,
            },

            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Surveys",
                message: "Get Started, Add a Survey",
              },
            },
          },

          toolbar: {
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/customer-surveys/create",
                clickAction: {},
                buttonLocation: "left",
                visible: false,
              },
            ],
            filterOnEnter: true,
          },
          columns: this.getColumns,
        },
      };

      return config;
    },

    getColumns() {
      return [
        {
          screenField: "Customer",
          sortIndex: 0,
          cellTemplateRecord: {
            primary: [
              {
                dataField: "CustomerName",
              },
            ],
            secondary: [
              {
                dataField: "CustomerCode",
              },
            ],
          },
          exportFields: [
            {
              dataField: "CustomerCode",
              label: "Customer Code",
            },
            {
              dataField: "CustomerName",
              label: "Customer Name",
            },
          ],
          fixed: true,
          fixedPosition: "left",
          allowHiding: false,
        },
        {
          screenField: "CustomerName",
          gridLabel: "CustomerNameFilter", // only added to bypass the having the same 'name' for 2 columns
          dataField: "CustomerName",
          visible: false,
          showInColumnChooser: false,
          dataType: "string",
          type: "Text",
          filterIndex: 0,
          filterDataField: "CustomerSearchText",
        },
        {
          screenField: "SurveyTemplateName",
          dataField: "SurveyTemplateName",
          filterDataField: "SurveyTemplateName",
          label: "Survey Name",
        },
        {
          screenField: "Date",
          dataField: "SurveyTemplateStartDate",
          dateVariant: "UTC",
          label: "Survey Start",
          filterLabel: "Survey Start",
          filterDataField: "SurveyTemplateStartDate",
          filterIndex: 2,
          sortOrder: "asc",
          filterOperator: "<=",
          filterDefaultValue: this.defaultDateFilterValue,
        },
        {
          screenField: "Date",
          dataField: "SurveyTemplateEndDate",
          dateVariant: "UTC",
          label: "Survey End",
          filterLabel: "Survey End",
          filterDataField: "SurveyTemplateEndDate",
          filterIndex: 3,
          filterOperator: "=",
          sortOrder: "asc",
        },
        {
          screenField: "Date",
          dataField: "StartDate",
          dateVariant: "UTC",
          label: "Response Opened",
          filterLabel: "Response Opened",
          filterDataField: "StartDate",
          filterIndex: 4,
          filterOperator: "=",
          maxWidth: 100,
        },
        {
          screenField: "Date",
          dataField: "EndDate",
          dateVariant: "UTC",
          filterLabel: "Response Completed",
          filterDataField: "EndDate",
          filterIndex: 5,
          label: "Response Completed",
          maxWidth: 100,
        },

        {
          screenField: "Date",
          dataField: "DateCreated",
          dateVariant: "UTC",
          // sortOrder: "desc",
          label: "Created Date",
          filterLabel: "Created Date",
          filterDataField: "DateCreated",
          visible: false,
          // filterIndex: 2,
          // filterOperator: ">",
          // filterDefaultValue: this.defaultDateFilterValue,
        },
        {
          screenField: "SalesRepresentative",
          label: "Sales Rep",
          dataField: "CustomerSalesRepresentativeCode",
          filterLabel: "Sales Rep",
          filterDataField: "CustomerSalesRepresentativeCode",
          filterIndex: 1,
          visible: false,
          showInColumnChooser: false,
          filterDefaultValue: this.getCustomerRepDefault,
        },
        {
          screenField: "SurveyTemplate",
          label: "Template",
          dataField: "SurveyTemplateRecordId",
          filterLabel: "Template",
          filterDataField: "SurveyTemplateRecordId",
          editorTemplate: {
            primary: [
              {
                dataField: "SurveyName",
              },
            ],
          },
          filterIndex: 6,
          searchExpr: ["SurveyName"],
          visible: false,
          showInColumnChooser: false,
        },
        {
          screenField: "CreatedBy",
          label: "Created By",
          dataField: "CreatedBy",
          filterLabel: "Created By",
          filterDataField: "CreatedBy",
          filterIndex: 7,
          maxWidth: ColumnWidth.RepCode.width,
          dataType: "string",
          type: "Text",
        },
        {
          screenField: "Compliance",
          dataField: "Compliance",
          filterLabel: "Compliance",
          filterDataField: "Compliance",
          label: "Compliance",
          filterIndex: 9,
          cellTemplate: "Status",
        },
        {
          screenField: "Status",
          gridDataField: "FriendlyStatus",
          gridLabel: "Status",
          dataField: "FriendlyStatus",
          filterDataField: "FriendlyStatus",
          filterIndex: 8,
          cellTemplate: "Status",
          dataSourceConfig: {
            data: [
              { key: "Active", value: "Active" },
              { key: "Completed", value: "Completed" },
              { key: "Pending", value: "Pending" },
            ],
          },
          valueExpr: "key",
          displayExpr: "value",
        },

        {
          screenField: "GridRecordEllipsisMenu",
          select: false,
          allowSearch: false,
          cssClass: "padding-7",
          ellipsisMenu: {
            itemAction: (data) => {
              var items = [
                {
                  menuText: "Customer List",
                  data: data,
                  clickAction: this.navigateToDestination,
                  disabled: false,
                },
              ];

              return items;
            },
            keyExpr: "menuText",
            displayExpr: "menuText",
          },
          // eslint-disable-next-line no-unused-vars
          makeDisabled: (data) => {
            // if (data) return data.data.Status != "C";
            return true;
          },
        },
      ];
    },
  },
  mounted() {
    logDev("mounted ", this.columns);
  },

  async created() {
    CommonStorage.clear("go_back_route");
    this.selectedIndex = this.tabs.findIndex((x) => x.text === "Surveys");

    await CustomerService.getByCode(this.baseView.id, ["Code", "Name"]).then(
      async (response) => {
        if (response.data.value.length > 0) {
          this.model.Code = response.data.value[0].Code;
          this.model.Name = response.data.value[0].Name;
        }
      }
    );
    await this.setDefaultCustomerRep();
    this.loaded = true;
  },
  methods: {
    surveyResponseListPostProcessCallback(data) {
      data.forEach((element) => {
        if (element.StartDate)
          element.StartDate = element.StartDate.dateWithOffset();

        if (element.EndDate) element.EndDate = element.EndDate.dateWithOffset();

        if (element.DateCreated)
          element.DateCreated = element.DateCreated.dateWithOffset();
      });
      return data;
    },
    async setDefaultCustomerRep() {
      var defaultSalesRep = CacheService.getCache(
        "UserDefaultSalesRepresentative"
      );

      if (!defaultSalesRep) {
        await SalesOrderListService.getUserDefaultSalesRepresentative()
          .then((result) => {
            logDev(
              "SalesOrderListService.getUserDefaultSalesRepresentative",
              result
            );
            this.setLoading = false;
            if (result.data.value[0].SalesRepresentativeCode != "") {
              this.CustomerSalesRepresentativeCode =
                result.data.value[0].SalesRepresentativeCode;
            }

            CacheService.saveCache(
              "UserDefaultSalesRepresentative",
              result.data.value[0],
              2
            );
            this.loaded = true;
          })
          .catch((err) => {
            logDev(
              "Error in SalesOrderListService.getUserDefaultSalesRepresentative",
              err
            );

            if (err) {
              this.setLoading = false;
              this.loaded = true;
            }
          });
      } else {
        if (defaultSalesRep.SalesRepresentativeCode != "")
          this.CustomerSalesRepresentativeCode =
            defaultSalesRep.SalesRepresentativeCode;

        this.loaded = true;
      }
    },
    backButtonFunction() {
      this.$router.push({
        path: this.backButtonRoute,
      });
    },
    rowClickCallBack(e) {
      if (
        e.rowType == "data" &&
        e.column.screenField != "Status" &&
        e.column.screenField != "GridRecordEllipsisMenu"
      ) {
        this.onRowClicked(e, {
          view: this,
          page: "surveys",
          mode: "review",
          idProperty: "RecordId",
          query: {
            cus_id: e.data.CustomerCode,
            from: "customer-surveys",
          },
        });
      }
    },
  },
};
</script>
